import Constants from "./Constants";

export default class CaptureImage {
    constructor(config, callback) {
        this.streaming = false;
        this.canvas = document.createElement("canvas");
        this.compressionValue = config.compressionValue;
        this.callback = callback;
        this.imageFrequencyInSeconds = config?.imageFrequencyInSeconds ?? 3;
    }

    init() {
        return new Promise((resolve, reject) => {
            this.setCamera().then((cameraId) => {
                this.cameraId = cameraId;
                const img = this.captureImageFromStream();
                resolve(img);
            }).catch((err) => {
                console.error(`CaptureImage: An error occurred: ${err}`);
                this.eventType = "error";
                reject(err);
            });
        });
    }

    setCamera(cameraId) {
        return new Promise((resolve, reject) => {
            if (!this.cameraId || cameraId !== this.cameraId) {
                const constraints = cameraId
                    ? {video: {deviceId: cameraId}, audio: false}
                    : {video: true, audio: false};

                navigator.mediaDevices?.getUserMedia(constraints)
                    .then((stream) => {
                        this.stopVideo();
                        this.createVideoElement();
                        this.video.srcObject = stream;
                        this.video?.play();
                        if (!this.cameraId) {
                            stream.getTracks().forEach(track => {
                                const cameraId = track.getCapabilities().deviceId;
                            });
                            resolve(cameraId);
                        }
                    })
                    .catch((err) => {
                        reject(err);
                    });
            } else {
                reject(`Camera with ID ${cameraId} is already set.`);
            }
        });
    }

    createVideoElement() {
        this.video = document.createElement("video");
        ['autoplay', 'muted', 'playsinline'].forEach(attr => this.video.setAttribute(attr, ''));
        ['abort', 'canplay', 'error', 'pause', 'play', 'stalled', 'ended', 'suspend']
            .forEach(eventType => this.createVideoEventListener(this.video, eventType));
    }

    stopVideo() {
        if (this.video) {
            this.video.pause();
            const videoStream = this.video.srcObject;
            videoStream?.getTracks().forEach(track => track.stop());
            this.video.srcObject = null;
            this.video.remove();
            this.video = null;
        }
    }

    createVideoEventListener(video, eventType) {
        video.addEventListener(eventType, () => {
            this.eventType = eventType;
            if (this.eventType === 'play' && !this.streaming) {
                this.streaming = true;
                this.captureImageFromStream();
            }
        }, false);
    }

    setImageFrequency(imageFrequencyInSeconds) {
        if (Number.isInteger(imageFrequencyInSeconds)) {
            this.imageFrequencyInSeconds = imageFrequencyInSeconds;
        }
    }

    async changeCaptureImageDevice(cameraId) {
        if (cameraId) {
            let deviceList = await navigator.mediaDevices.enumerateDevices();
            for (const _device of deviceList) {
                if (_device.deviceId == cameraId) {
                    this.setCamera(cameraId);
                }
            }
        }
    }

    captureImageFromStream() {
        let newImageData = null;
        if (['canplay', 'play'].includes(this.eventType)) {
            const mimeType = 'image/jpeg';
            this.canvas.width = this.canvas.height = 360;

            const ctx = this.canvas.getContext("2d");
            ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
            ctx.drawImage(this.video, 0, 0, this.canvas.width, this.canvas.height);

            newImageData = this.canvas.toDataURL(mimeType, this.compressionValue / 100);
            if (newImageData.length > Constants.ImageCapture.MAX_IMAGE_SIZE) {
                const decreasePercentage = Constants.ImageCapture.MAX_IMAGE_SIZE / (newImageData.length * 1.5);
                const decreasedCompressionValue = Math.round((this.compressionValue * decreasePercentage) - 1);
                newImageData = this.canvas.toDataURL(mimeType, decreasedCompressionValue / 100);
            }
        } else {
            //These messages are being sent to the proctor so they need to be translated to english
            const options = { lng: 'en' };
            const message1 = this.eventType === undefined ? rtp_i18next.t("generalWords:callConnecting", options) : rtp_i18next.t("proctorWidget:cameraNotWorking", options);
            const message2 = this.eventType === undefined ? rtp_i18next.t("generalWords:pleaseWait", options) : rtp_i18next.t("proctorWidget:noImageAvailable", options);
            newImageData = CaptureImage.textToImage(message1, message2);

            const constraints = this.cameraId
                ? { video: { deviceId: this.cameraId }, audio: false }
                : { video: true, audio: false };

            navigator.mediaDevices?.getUserMedia(constraints)
                .then((stream) => {
                    const camTracks = stream.getTracks();
                    const restartVideo = camTracks.some(track => {
                        if (!this.cameraId) this.cameraId = track.getCapabilities().deviceId;
                        return !this.cameraId || track.getCapabilities().deviceId === this.cameraId;
                    });

                    if (restartVideo) {
                        this.stopVideo();
                        this.createVideoElement();
                        this.video.srcObject = stream;
                        this.video.play();
                    }
                })
                .catch((err) => console.error(`CaptureImage: An error occurred: ${err}`));
        }

        return newImageData;
    }

    setStreaming(streaming) {
        if (this.streaming !== streaming) {
            this.streaming = streaming;
            if (this.video) {
                if (this.streaming) {
                    this.video.play();
                } else {
                    this.stopVideo();
                }
            }
        }
    }

    static textToImage(displayTextLine1, displayTextLine2) {
        var canvas=  document.createElement("canvas");
        canvas.width = 480;
        canvas.height = 360;
        var ctx = canvas.getContext("2d");
        ctx.font = "30px Arial, sans-serif";
        ctx.fillStyle = "white";
        ctx.textAlign = "center";
        if (displayTextLine2 && displayTextLine2.length > 0) {
            ctx.fillText(displayTextLine1, canvas.width / 2, ((canvas.height / 2) - 20));
            ctx.fillText(displayTextLine2, canvas.width / 2, ((canvas.height / 2) + 20));
        } else {
            ctx.fillText(displayTextLine1, canvas.width / 2, canvas.height);
        }
        const imageToDisplay = canvas.toDataURL('image/jpeg');
        return imageToDisplay;
    }

    destroy() {
        this.stopVideo();
        this.canvas = null;
        this.callback = null;
    }
}
